import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Character, Section } from '../types/character';
import { Universe, ThemeColor, themeColors, universeLabels, universeCharacteristics } from '../types/template';
import { PlusCircle, X, ImageIcon, Palette, Trash2, Save } from 'lucide-react';
import { ImageUpload } from './ImageUpload';
import { TextEditor } from './TextEditor';

interface Props {
  character: Character;
  onUpdate: (character: Character) => void;
  onClose: () => void;
}

const AUTOSAVE_KEY = 'character-editor-autosave';

export function CharacterEditor({ character, onUpdate, onClose }: Props) {
  const [localCharacter, setLocalCharacter] = useState<Character>(() => ({
    ...character,
    themeColor: character.themeColor || 'purple',
    containerBackground: character.containerBackground || { type: 'color', value: 'bg-white' },
    illustrations: character.illustrations || [],
    customSections: character.customSections || [],
    lastModified: Date.now()
  }));
  const [newSectionTitle, setNewSectionTitle] = useState('');
  const [newIllustrationUrl, setNewIllustrationUrl] = useState('');
  const [newIllustrationCaption, setNewIllustrationCaption] = useState('');
  const [showContainerOptions, setShowContainerOptions] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Charger la sauvegarde automatique au montage
  useEffect(() => {
    const savedData = localStorage.getItem(`${AUTOSAVE_KEY}-${character.id}`);
    if (savedData) {
      try {
        const parsedData = JSON.parse(savedData);
        if (parsedData.id === character.id) {
          setLocalCharacter(parsedData);
          setHasUnsavedChanges(true);
        }
      } catch (e) {
        console.error('Erreur lors du chargement de la sauvegarde automatique:', e);
      }
    }
  }, [character.id]);

  // Sauvegarder automatiquement les modifications
  useEffect(() => {
    if (hasUnsavedChanges) {
      localStorage.setItem(`${AUTOSAVE_KEY}-${character.id}`, JSON.stringify(localCharacter));
    }
  }, [localCharacter, hasUnsavedChanges, character.id]);

  // Nettoyer la sauvegarde automatique
  const clearAutosave = () => {
    localStorage.removeItem(`${AUTOSAVE_KEY}-${character.id}`);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onUpdate(localCharacter);
      clearAutosave();
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      alert('Une erreur est survenue lors de la sauvegarde. Veuillez réessayer.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (hasUnsavedChanges) {
      const shouldSave = window.confirm(
        'Des modifications non sauvegardées ont été détectées. Voulez-vous les sauvegarder avant de fermer ?'
      );
      
      if (shouldSave) {
        handleSave().then(() => onClose());
      } else {
        clearAutosave();
        onClose();
      }
    } else {
      onClose();
    }
  };

  const updateLocalCharacter = (updates: Partial<Character>) => {
    setLocalCharacter(prev => ({
      ...prev,
      ...updates,
      lastModified: Date.now()
    }));
    setHasUnsavedChanges(true);
  };

  const handleNameChange = (name: string) => {
    updateLocalCharacter({ name });
  };

  const handleThemeChange = (themeColor: ThemeColor) => {
    updateLocalCharacter({ themeColor });
  };

  const handleContainerBackgroundChange = (type: 'color' | 'image', value: string) => {
    updateLocalCharacter({ containerBackground: { type, value } });
  };

  const handleImageChange = (type: 'profile' | 'background', url: string) => {
    updateLocalCharacter({
      [type === 'profile' ? 'profileImage' : 'backgroundImage']: url
    });
  };

  const handleSectionChange = (id: string, content: string, isCustom: boolean = false) => {
    const sections = isCustom ? 'customSections' : 'sections';
    updateLocalCharacter({
      [sections]: localCharacter[sections].map((section) =>
        section.id === id ? { ...section, content } : section
      )
    });
  };

  const addCustomSection = () => {
    if (!newSectionTitle.trim()) return;
    
    updateLocalCharacter({
      customSections: [
        ...localCharacter.customSections,
        {
          id: Date.now().toString(),
          title: newSectionTitle,
          content: ''
        }
      ]
    });
    setNewSectionTitle('');
  };

  const deleteCustomSection = (id: string) => {
    updateLocalCharacter({
      customSections: localCharacter.customSections.filter(section => section.id !== id)
    });
  };

  const handleUniverseChange = (universe: Universe) => {
    const characteristics = universeCharacteristics[universe].map((char, index) => ({
      id: `char-${index + 3}`,
      title: char,
      content: ''
    }));

    updateLocalCharacter({
      universe,
      sections: [
        { id: '1', title: 'Description Physique', content: localCharacter.sections[0]?.content || '' },
        { id: '2', title: 'Personnalité', content: localCharacter.sections[1]?.content || '' },
        ...characteristics
      ]
    });
  };

  const handleAddIllustration = () => {
    if (!newIllustrationUrl.trim()) return;

    updateLocalCharacter({
      illustrations: [
        ...localCharacter.illustrations,
        {
          id: Date.now().toString(),
          url: newIllustrationUrl,
          caption: newIllustrationCaption
        }
      ]
    });
    setNewIllustrationUrl('');
    setNewIllustrationCaption('');
  };

  const handleRemoveIllustration = (id: string) => {
    updateLocalCharacter({
      illustrations: localCharacter.illustrations.filter(ill => ill.id !== id)
    });
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/20 backdrop-blur-sm z-50 overflow-y-auto">
      <div className="min-h-screen px-4 py-8">
        <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-xl">
          <div className="flex justify-between items-center p-4 border-b">
            <h2 className="text-xl font-semibold">Modifier le personnage</h2>
            <div className="flex items-center gap-4">
              <button
                onClick={handleSave}
                disabled={!hasUnsavedChanges || isSaving}
                className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                  !hasUnsavedChanges || isSaving
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-purple-600 text-white hover:bg-purple-700'
                }`}
              >
                {isSaving ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                    Sauvegarde...
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5" />
                    Sauvegarder
                  </>
                )}
              </button>
              <button
                onClick={handleClose}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          <div className="p-6 space-y-8">
            {/* Character Name, Universe, and Theme */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nom du personnage
                </label>
                <input
                  type="text"
                  value={localCharacter.name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                  placeholder="Nom du personnage"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Univers
                </label>
                <select
                  value={localCharacter.universe}
                  onChange={(e) => handleUniverseChange(e.target.value as Universe)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                >
                  {Object.entries(universeLabels).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Thème de couleur
                </label>
                <div className="grid grid-cols-5 gap-3">
                  {(Object.keys(themeColors) as ThemeColor[]).map((color) => (
                    <button
                      key={color}
                      onClick={() => handleThemeChange(color)}
                      className={`w-full aspect-square rounded-lg ${themeColors[color].bg} 
                        ${localCharacter.themeColor === color ? 'ring-4 ring-offset-2 ring-purple-500' : ''}`}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Container Background */}
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium text-gray-900">Arrière-plan du conteneur</h3>
                <button
                  onClick={() => setShowContainerOptions(!showContainerOptions)}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                >
                  <Palette className="w-4 h-4" />
                  {showContainerOptions ? 'Masquer les options' : 'Afficher les options'}
                </button>
              </div>

              {showContainerOptions && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Type d'arrière-plan
                    </label>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          checked={localCharacter.containerBackground?.type === 'color'}
                          onChange={() => handleContainerBackgroundChange('color', 'bg-white')}
                          className="text-purple-600 focus:ring-purple-500"
                        />
                        <span>Couleur</span>
                      </label>
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          checked={localCharacter.containerBackground?.type === 'image'}
                          onChange={() => handleContainerBackgroundChange('image', '')}
                          className="text-purple-600 focus:ring-purple-500"
                        />
                        <span>Image</span>
                      </label>
                    </div>
                  </div>

                  {localCharacter.containerBackground?.type === 'color' ? (
                    <div className="grid grid-cols-5 gap-3">
                      {containerColors.map((color) => (
                        <button
                          key={color.value}
                          onClick={() => handleContainerBackgroundChange('color', color.value)}
                          className={`w-full aspect-square rounded-lg ${color.value} border-2 
                            ${localCharacter.containerBackground?.value === color.value 
                              ? 'border-purple-500' 
                              : 'border-transparent'}`}
                          title={color.name}
                        />
                      ))}
                    </div>
                  ) : (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        URL de l'image d'arrière-plan
                      </label>
                      <input
                        type="text"
                        value={localCharacter.containerBackground?.value || ''}
                        onChange={(e) => handleContainerBackgroundChange('image', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        placeholder="https://exemple.com/image.jpg"
                      />
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Images */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <ImageUpload
                currentImage={localCharacter.profileImage}
                onChange={(url) => handleImageChange('profile', url)}
                label="Image de profil"
                aspectRatio={1}
              />
              <ImageUpload
                currentImage={localCharacter.backgroundImage}
                onChange={(url) => handleImageChange('background', url)}
                label="Image de fond"
                aspectRatio={16/9}
              />
            </div>

            {/* Description et Personnalité */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {localCharacter.sections.slice(0, 2).map((section) => (
                <div key={section.id} className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    {section.title}
                  </label>
                  <TextEditor
                    value={section.content}
                    onChange={(content) => handleSectionChange(section.id, content)}
                    placeholder={`${section.title.toLowerCase()}...`}
                    minHeight="150px"
                  />
                </div>
              ))}
            </div>

            {/* Caractéristiques spécifiques à l'univers */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">
                Caractéristiques de l'univers {universeLabels[localCharacter.universe]}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {localCharacter.sections.slice(2).map((section) => (
                  <div key={section.id} className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      {section.title}
                    </label>
                    <TextEditor
                      value={section.content}
                      onChange={(content) => handleSectionChange(section.id, content)}
                      placeholder={`${section.title}...`}
                      minHeight="100px"
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Section Histoire */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Histoire</h3>
              <TextEditor
                value={localCharacter.history || ''}
                onChange={(content) => {
                  updateLocalCharacter({ history: content });
                }}
                placeholder="Racontez l'histoire de votre personnage..."
                minHeight="200px"
              />
            </div>

            {/* Section Illustrations */}
            <div className="space-y-6">
              <h3 className="text-lg font-medium text-gray-900">Illustrations</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {localCharacter.illustrations.map((illustration) => (
                  <div key={illustration.id} className="relative group">
                    <img
                      src={illustration.url}
                      alt={illustration.caption}
                      className="w-full h-48 object-cover rounded-lg"
                    />
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                      <button
                        onClick={() => handleRemoveIllustration(illustration.id)}
                        className="p-3 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                      >
                        <X className="w-6 h-6" />
                      </button>
                    </div>
                    {illustration.caption && (
                      <p className="mt-2 text-sm text-gray-600">{illustration.caption}</p>
                    )}
                  </div>
                ))}
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    type="text"
                    value={newIllustrationUrl}
                    onChange={(e) => setNewIllustrationUrl(e.target.value)}
                    placeholder="URL de l'image"
                    className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                  <input
                    type="text"
                    value={newIllustrationCaption}
                    onChange={(e) => setNewIllustrationCaption(e.target.value)}
                    placeholder="Légende (optionnelle)"
                    className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                </div>
                <button
                  onClick={handleAddIllustration}
                  className="w-full px-4 py-4 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center gap-2 text-lg"
                >
                  <ImageIcon className="w-6 h-6" />
                  Ajouter une illustration
                </button>
              </div>
            </div>

            {/* Sections personnalisées */}
            {localCharacter.customSections.length > 0 && (
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900">Sections personnalisées</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {localCharacter.customSections.map((section) => (
                    <div key={section.id} className="space-y-2">
                      <div className="flex justify-between items-center">
                        <label className="block text-sm font-medium text-gray-700">
                          {section.title}
                        </label>
                        <button
                          onClick={() => deleteCustomSection(section.id)}
                          className="p-1 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Supprimer cette section"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      <TextEditor
                        value={section.content}
                        onChange={(content) => handleSectionChange(section.id, content, true)}
                        placeholder={`Contenu pour ${section.title}...`}
                        minHeight="150px"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Ajouter une section personnalisée */}
            <div className="flex gap-3">
              <input
                type="text"
                value={newSectionTitle}
                onChange={(e) => setNewSectionTitle(e.target.value)}
                className="flex-1 px-4 py-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                placeholder="Titre de la nouvelle section"
              />
              <button
                onClick={addCustomSection}
                className="px-6 py-4 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2 text-lg"
              >
                <PlusCircle className="w-6 h-6" />
                Ajouter
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}